import type { JSXElementConstructor, ReactElement } from "react";
import { controlCharsObject, isControlChar as isControlCharKeyword, notDisplayedKeywords } from "@quickpoint.me/shared";

import type { ElementProps } from "../functionalComponents/RenderPage";
import { type ContentContext, TUFTE_VERTICAL_PAD_FRACTION, type GridObject, computeMediaWeight, isMediaType, isTextType } from "./gridTypes";

export function calculateLandscapeRowsAndColumns(
  elements: ReactElement<ElementProps>[],
  contentContext?: ContentContext,
): GridObject[] {

  const gridArray: GridObject[] = [];
  const splitIndices: number[] = [];
  elements.forEach((el, idx) => {
    if (el.props.type === controlCharsObject["|"].controlChar) {
      splitIndices.push(idx);
    }
  });

  const rowsIndices: number[] = [];
  elements.forEach((el, idx) => {
    if (el.props.type === controlCharsObject["---"].controlChar) {
      rowsIndices.push(idx);
    }
  });

  const endOfGridIndices: number[] = [];
  elements.forEach((el, idx) => {
    if (el.props.type === controlCharsObject["-->"].controlChar) {
      endOfGridIndices.push(idx);
    }
  });

  // multiple grids on one page interleave --- and --> indices, so sort
  const allRowIndices = rowsIndices.concat(endOfGridIndices).sort((a, b) => a - b)


  let rowIdx = -1;
  let gridCol = 1;
  let gridRow = 1;
  let isSplitCell = false
  elements.every((el, i) => {
    if (el.props.type === controlCharsObject["-->"].controlChar) return true
    if (el.props.isAutospaceElement) {
      // autospace between stacked elements of a split cell — skipped when the
      // next element starts a new cell/row or isn't displayed content
      const nextType = elements[i + 1]?.props.type as string | undefined;
      const nextIsContent = nextType !== undefined &&
        !isControlCharKeyword(nextType) &&
        nextType !== "background:" &&
        !notDisplayedKeywords.includes(nextType);
      const currentCell = gridArray[gridArray.length - 1];
      if (isSplitCell && nextIsContent && currentCell && currentCell.elArray.length > 0) {
        currentCell.elArray.push(el);
      }
      return true;
    }

    if (notDisplayedKeywords.includes(el.props.type as string)) return true
    let isControlChar = false

    if (el.props.type === controlCharsObject["---"].controlChar || el.props.type === controlCharsObject["^^^"].controlChar) {
      rowIdx++
      if (rowIdx > 0) gridRow++;
      gridCol = 1
      isSplitCell = false
      isControlChar = true
    }

    const thisRow = allRowIndices[rowIdx]
    // a grid without a trailing --> has no boundary after its last row
    const nextRow = allRowIndices[rowIdx + 1] ?? Number.POSITIVE_INFINITY;
    const split = splitIndices.find(s => s > thisRow && s < nextRow)
    if (split !== undefined) {
      isSplitCell = true
    }

    if (el.props.type === controlCharsObject["|"].controlChar) {
      gridCol++
      isControlChar = true
    }

    if (el.props.type === "background:") {
      if (gridArray[gridArray.length - 1]) gridArray[gridArray.length - 1].elArray.push(el);
      return true;
    }

    const cell = {
      col: gridCol,
      row: gridRow,
      elArray: [el],
      lastColInRow: false,
      lastRowInGrid: false,
      isSplitCell,
      maxCols: 0,
      maxRows: 0,
    }


    if (!isControlChar && !isSplitCell) {
      gridArray.push(cell)
    }

    if (isControlChar && isSplitCell) {
      cell.elArray = []
      gridArray.push(cell)
    }

    if (!isControlChar && isSplitCell) {
      gridArray[gridArray.length - 1].elArray.push(el)
    }

    if (!isControlChar && !isSplitCell) {
      gridCol++
    }

    return true;
  });

  const rawMaxCols = gridArray.reduce((acc, curr) => {
    if (curr.col > acc) return curr.col;
    return acc;
  }, 0);

  const maxRows = gridArray.reduce((acc, curr) => {
    if (curr.row > acc) return curr.row;
    return acc;
  }, 0);

  function checkForBackdrop(elArray: ReactElement<ElementProps, string | JSXElementConstructor<unknown>>[]) {
    const backdropIndex = elArray.findIndex((el: ReactElement<ElementProps, string | JSXElementConstructor<unknown>>) => el.props.type === "background:")
    if (backdropIndex === -1) return elArray
    const backdrop = elArray.splice(backdropIndex, 1)[0];
    elArray.unshift(backdrop);
    return elArray
  }

  const rowMap = new Map<number, GridObject[]>();
  for (const g of gridArray) {
    if (!rowMap.has(g.row)) rowMap.set(g.row, []);
    rowMap.get(g.row)?.push(g);
  }

  // Calculate LCM for grid resolution
  const gcd = (a: number, b: number): number => (!b ? a : gcd(b, a % b));
  const lcm = (a: number, b: number): number => (a * b) / gcd(a, b);

  const implicitRowCounts: number[] = [];
  for (const cells of rowMap.values()) {
    if (!cells.some(c => c.isSplitCell) && cells.length > 0) {
      implicitRowCounts.push(cells.length);
    }
  }

  let targetMaxCols = rawMaxCols;
  if (targetMaxCols > 0) {
    for (const count of implicitRowCounts) {
      targetMaxCols = lcm(targetMaxCols, count);
    }
  }

  const scaleFactor = rawMaxCols > 0 ? targetMaxCols / rawMaxCols : 1;

  for (const cells of rowMap.values()) {
    const isImplicit = !cells.some(c => c.isSplitCell);

    if (isImplicit && cells.length > 0) {
      // Auto-flow: distribute evenly across targetMaxCols
      const span = targetMaxCols / cells.length;
      let i = 0;
      for (const cell of cells) {
        cell.colSpan = span;
        cell.col = (i * span) + 1;
        i++;
      }
    } else {
      // Explicit or mixed: scale existing positions
      for (const cell of cells) {
        // Scale the column index
        cell.col = (cell.col - 1) * scaleFactor + 1;
        // Scale the span (default was 1 in raw grid)
        cell.colSpan = scaleFactor;
      }
    }
  }

  gridArray.forEach((el, idx) => {
    el.elArray = checkForBackdrop(el.elArray)
    el.maxCols = targetMaxCols;
    el.maxRows = maxRows;
    if (gridArray[idx + 1] && el.row !== gridArray[idx + 1].row) {
      el.lastColInRow = true;
    }
    if (!gridArray[idx + 1]) {
      el.lastColInRow = true;
      el.lastRowInGrid = true;
    }
  });

  // Tufte media sizing: weight mixed text+media rows individually. Each
  // weightable row gets col/colSpan recomputed from its own word count, on a
  // subdivided track grid so different rows can carry different weights.
  if (contentContext && targetMaxCols > 1) {
    const cellHasMedia = (c: GridObject) => c.elArray.some(el => isMediaType(el.props.type));
    const rowWordCount = (cells: GridObject[]) =>
      cells.reduce((sum, c) => sum + c.elArray.reduce((s, el) =>
        s + (isTextType(el.props.type) ? contentContext.wordCounts.get(el.props.atomId) ?? 0 : 0), 0), 0);

    const mixedRows = Array.from(rowMap.values()).filter(cells =>
      cells.length > 1 &&
      cells.some(cellHasMedia) &&
      rowWordCount(cells) > 0
    );

    if (mixedRows.length > 0) {
      // subdivide tracks so weighted spans can express ~1% row fractions;
      // targetMaxCols is the LCM of implicit row counts, so multiplying keeps
      // every unweighted row's spans integral
      const fineScale = Math.max(1, Math.floor(120 / targetMaxCols));
      const totalTracks = targetMaxCols * fineScale;
      for (const g of gridArray) {
        g.col = (g.col - 1) * fineScale + 1;
        g.colSpan = (g.colSpan ?? 1) * fineScale;
        g.maxCols = totalTracks;
        g.hasWeightedRows = true;
      }

      // height-aware cap: an image in a row can never render wider than
      // rowHeight × aspect, so don't allocate width it can't use — hand the
      // surplus to the text columns instead
      const { mediaAspects, slideWidth, slideHeight } = contentContext;
      // weighted grids render with padding-block, so rows share the padded height
      const paddedHeight = slideHeight ? slideHeight * (1 - 2 * TUFTE_VERTICAL_PAD_FRACTION) : 0;
      const rowHeight = slideWidth && paddedHeight && maxRows > 0 ? paddedHeight / maxRows : 0;
      const capForCell = (c: GridObject): number => {
        if (!rowHeight || !mediaAspects || !slideWidth) return 100;
        const aspects = c.elArray
          .filter(el => el.props.type === "image:")
          .map(el => mediaAspects[el.props.atomId])
          .filter((a): a is number => typeof a === "number" && a > 0);
        if (aspects.length === 0) return 100;
        const usableWidth = rowHeight * Math.max(...aspects);
        return Math.max(20, Math.round((usableWidth / slideWidth) * 100));
      };

      for (const cells of mixedRows) {
        const baseWeight = computeMediaWeight(rowWordCount(cells), contentContext.baseFontSize);
        const mediaWeights = new Map<GridObject, number>();
        for (const c of cells) {
          if (cellHasMedia(c)) mediaWeights.set(c, Math.min(baseWeight, capForCell(c)));
        }
        const rowMediaWeight = Math.max(...mediaWeights.values());
        const weights = cells.map(c => mediaWeights.get(c) ?? 100 - rowMediaWeight);
        const weightSum = weights.reduce((a, b) => a + b, 0);
        let col = 1;
        cells.forEach((cell, i) => {
          const span = i === cells.length - 1
            ? Math.max(1, totalTracks - col + 1)
            : Math.max(1, Math.round((weights[i] / weightSum) * totalTracks));
          cell.col = col;
          cell.colSpan = span;
          col += span;
        });
      }
    }
  }

  return gridArray;
}
